// Life & Wealth Solutions V22 - Authentication Hook
// UPDATED: Session 6 - Integrated with Backend Auth API (Session 5)
// Manages user authentication state with JWT tokens and refresh

import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import axios from 'axios';

interface User {
  user_id: string;
  email: string;
  full_name: string;
  role: 'agent12' | 'admin' | 'viewer';
  agency_id: string;
  is_active: boolean;
  last_login?: string;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<void>;
  isAgent12: boolean;
  isAdmin: boolean;
  isViewer: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  role: 'agent12' | 'admin' | 'viewer';
  agency_id: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base URL - Python FastAPI backend (Session 5)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Auto refresh token before expiry (25 minutes - token expires at 30)
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(() => {
      refreshToken();
    }, 25 * 60 * 1000); // 25 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (token) {
        try {
          // Verify token and get user data
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Try to refresh token
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              await refreshTokenInternal(refreshToken);
            } catch (refreshError) {
              // Refresh failed, clear everything
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            }
          } else {
            localStorage.removeItem('access_token');
          }
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });

      const { access_token, refresh_token } = response.data;
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Configure axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Get user data
      const userResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      // Set user state
      setUser(userResponse.data);
      
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const register = async (data: RegisterData) => {
    try {
      // Register user
      const registerResponse = await axios.post(`${API_URL}/auth/register`, data);

      // Auto-login after registration
      await login(data.email, data.password);
      
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      const token = localStorage.getItem('access_token');
      if (token) {
        await axios.post(`${API_URL}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear tokens regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      
      // Clear user state
      setUser(null);
      
      // Clear axios defaults
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const refreshTokenInternal = async (refreshTokenStr: string) => {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refresh_token: refreshTokenStr
      });

      const { access_token } = response.data;
      
      // Store new access token
      localStorage.setItem('access_token', access_token);
      
      // Configure axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Get updated user data
      const userResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      setUser(userResponse.data);
      
    } catch (error: any) {
      throw new Error('Token refresh failed');
    }
  };

  const refreshToken = async () => {
    const refreshTokenStr = localStorage.getItem('refresh_token');
    if (!refreshTokenStr) {
      throw new Error('No refresh token available');
    }

    await refreshTokenInternal(refreshTokenStr);
  };

  const updateUser = async (data: Partial<User>) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.patch(`${API_URL}/users/me`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Update failed');
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    updateUser,
    isAgent12: user?.role === 'agent12' || user?.role === 'admin',
    isAdmin: user?.role === 'admin',
    isViewer: user?.role === 'viewer'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
